// Defer loading pdfjs until methods are called to keep initial bundle small
async function getPdfJs() {
  const pdfjsLib = await import('pdfjs-dist');
  return pdfjsLib;
}

/**
 * Renders the PinIcon SVG to a canvas image
 * @param color - Hex color code for the pin (e.g., "#ef4444")
 * @param size - Size of the pin in pixels
 * @returns Promise<HTMLImageElement> - Image element with the rendered pin
 */
async function renderPinIconToImage(color: string, size: number = 32): Promise<HTMLImageElement> {
  // Create SVG string with dynamic color
  const svgString = `
    <svg
      width="${size}"
      height="${size * (102 / 64)}"
      viewBox="0 0 64 102"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      xml:space="preserve"
      style="fill-rule:evenodd;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:2;"
    >
      <g>
        <!-- Shadow/highlight path 1 - Fixed color -->
        <g transform="matrix(4.166667,0,0,4.166667,-2113.631682,-1507.841682)">
          <path 
            d="M514.94,361.882C517.642,361.882 520.143,363.289 521.529,365.588C522.916,367.924 522.982,370.804 521.706,373.176L514.94,386.294L514.94,361.882Z" 
            style="fill:rgb(80,133,196);fill-rule:nonzero;"
          />
        </g>
        <!-- Shadow/highlight path 2 - Fixed color -->
        <g transform="matrix(4.166667,0,0,4.166667,-2113.631682,-1507.841682)">
          <path 
            d="M514.94,361.882C512.24,361.882 509.74,363.289 508.353,365.588C506.966,367.924 506.899,370.804 508.176,373.176L514.94,386.294L514.94,361.882Z" 
            style="fill:rgb(49,122,191);fill-rule:nonzero;"
          />
        </g>
        <!-- Main pin circle - Dynamic color from prop -->
        <g transform="matrix(4.166667,0,0,4.166667,-2113.631682,-1507.841682)">
          <path 
            d="M515.765,365.059C516.549,365.216 517.255,365.549 517.882,366.059C518.51,366.608 518.961,367.255 519.235,368C519.51,368.784 519.568,369.569 519.412,370.353C519.255,371.137 518.922,371.843 518.412,372.471C517.862,373.098 517.216,373.549 516.471,373.824C515.685,374.098 514.901,374.157 514.117,374C513.333,373.843 512.627,373.51 512,373C511.373,372.451 510.922,371.804 510.647,371.059C510.412,370.275 510.353,369.49 510.471,368.706C510.627,367.922 510.961,367.216 511.471,366.588C512.02,365.961 512.667,365.51 513.412,365.235C514.195,365 514.979,364.941 515.765,365.059Z" 
            style="fill:${color};fill-rule:nonzero;"
          />
        </g>
        <!-- White stroke outline - Fixed -->
        <g transform="matrix(4.166667,0,0,4.166667,-2113.631682,-1507.841682)">
          <path 
            d="M515.765,365.059C516.549,365.216 517.255,365.549 517.882,366.059C518.51,366.608 518.961,367.255 519.235,368C519.51,368.784 519.568,369.569 519.412,370.353C519.255,371.137 518.922,371.843 518.412,372.471C517.862,373.098 517.216,373.549 516.471,373.824C515.685,374.098 514.901,374.157 514.117,374C513.333,373.843 512.627,373.51 512,373C511.373,372.451 510.922,371.804 510.647,371.059C510.412,370.275 510.353,369.49 510.471,368.706C510.627,367.922 510.961,367.216 511.471,366.588C512.02,365.961 512.667,365.51 513.412,365.235C514.195,365 514.979,364.941 515.765,365.059Z" 
            style="fill:none;stroke:white;stroke-width:1.41px;"
          />
        </g>
      </g>
    </svg>
  `;

  // Convert SVG string to data URL
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  // Create image element and load the SVG
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    img.src = url;
  });
}

export class PlanImageCaptureService {
  private static readonly DEFAULT_PREVIEW_OUTPUT_SCALE = 1.5;
  private static readonly DEFAULT_PREVIEW_JPEG_QUALITY = 0.9;

  private static clampQuality(value: number): number {
    return Math.max(0, Math.min(1, value));
  }

  /**
   * Captures a section of the PDF plan around the pin location
   * @param planFileUrl - The base64 data URL of the plan PDF
   * @param pinLocation - The pin location coordinates
   * @param zoomLevel - The zoom level to capture at (default: 2 for better detail)
   * @param captureSize - The size of the area to capture around the pin
   * @param ticketNumber - Optional ticket number to render in the pin
   * @param pinColor - Optional hex color code for the pin (default: "#ef4444" for red/open status)
   * @param outputOptions - Optional output quality/format settings
   * @returns Promise<{imageData: string, pinPosition: {x: number, y: number}}> - Base64 image data and pin position within the image
   */
  static async capturePlanSection(
    planFileUrl: string,
    pinLocation: { x: number; y: number; planId: string; zoom: number },
    zoomLevel: number = 2,
    captureSize: { width: number; height: number } = { width: 400, height: 300 },
    ticketNumber?: string,
    pinColor: string = '#ef4444',
    outputOptions?: {
      outputScale?: number;
      outputFormat?: 'image/jpeg' | 'image/webp' | 'image/png';
      imageQuality?: number;
    }
  ): Promise<{imageData: string, pinPosition: {x: number, y: number}}> {
    try {
      const pdfjsLib = await getPdfJs();
      // Set up PDF.js worker
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

      // Load the PDF document
      const loadingTask = pdfjsLib.getDocument(planFileUrl);
      const pdf = await loadingTask.promise;

      // Get the first page (assuming single page plans)
      const page = await pdf.getPage(1);
      
      // Create a canvas to render the PDF
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      if (!context) {
        throw new Error('Could not get canvas context');
      }

      // Calculate the viewport with the desired zoom level
      const viewport = page.getViewport({ scale: zoomLevel });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Render the PDF page to canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
        canvas,
      };

      await page.render(renderContext).promise;

      // Map stored pin coordinates (center-based with 500 offset) to canvas pixel coordinates
      // Stored system: center at (500, 500); offsets measured in base PDF pixels
      // When zoomLevel > 1, we need to scale the offset by zoomLevel to convert PDF pixels to canvas pixels
      const canvasCenterX = canvas.width / 2;
      const canvasCenterY = canvas.height / 2;
      const pinCanvasX = canvasCenterX + (pinLocation.x - 500) * zoomLevel;
      const pinCanvasY = canvasCenterY + (pinLocation.y - 500) * zoomLevel;

      // console.log('PlanImageCaptureService - Pin canvas coordinates:', { pinCanvasX, pinCanvasY });
      // console.log('PlanImageCaptureService - Canvas dimensions:', { width: canvas.width, height: canvas.height });

      // Calculate the capture area bounds centered around the pin (clamped to canvas)
      const halfW = captureSize.width / 2;
      const halfH = captureSize.height / 2;
      const unclampedX = pinCanvasX - halfW;
      const unclampedY = pinCanvasY - halfH;
      const captureX = Math.max(0, Math.min(unclampedX, canvas.width - captureSize.width));
      const captureY = Math.max(0, Math.min(unclampedY, canvas.height - captureSize.height));
      const captureWidth = Math.min(captureSize.width, canvas.width - captureX);
      const captureHeight = Math.min(captureSize.height, canvas.height - captureY);
      
      // console.log('PlanImageCaptureService - Capture bounds:', { captureX, captureY, captureWidth, captureHeight });

      // Create a new canvas for the captured section
      const captureCanvas = document.createElement('canvas');
      const captureContext = captureCanvas.getContext('2d');
      
      if (!captureContext) {
        throw new Error('Could not get capture canvas context');
      }

      const outputScale = Math.max(1, outputOptions?.outputScale ?? this.DEFAULT_PREVIEW_OUTPUT_SCALE);
      const outputWidth = Math.max(1, Math.round(captureWidth * outputScale));
      const outputHeight = Math.max(1, Math.round(captureHeight * outputScale));

      captureCanvas.width = outputWidth;
      captureCanvas.height = outputHeight;

      captureContext.imageSmoothingEnabled = true;
      captureContext.imageSmoothingQuality = 'high';
      captureContext.setTransform(outputScale, 0, 0, outputScale, 0, 0);

      // Draw the captured section
      captureContext.drawImage(
        canvas,
        captureX, captureY, captureWidth, captureHeight,
        0, 0, captureWidth, captureHeight
      );

      // Calculate pin position within the captured image (account for clamping)
      const pinOffsetXInCapture = pinCanvasX - captureX;
      const pinOffsetYInCapture = pinCanvasY - captureY;
      const pinPositionInImage = {
        x: Math.max(0, Math.min(100, (pinOffsetXInCapture / captureWidth) * 100)),
        y: Math.max(0, Math.min(100, (pinOffsetYInCapture / captureHeight) * 100))
      };

      // Render pin directly on the captured image if ticketNumber is provided
      if (ticketNumber !== undefined) {
        const pinX = pinOffsetXInCapture;
        const pinY = pinOffsetYInCapture;
        
        // Pin size scales with zoom level (larger pin at higher zoom)
        // Base size is 32px at zoom 2, scales proportionally
        const pinSize = Math.max(24, 32 * (zoomLevel / 2));
        
        try {
          // Render PinIcon SVG to image
          const pinImage = await renderPinIconToImage(pinColor, pinSize);
          
          // Draw the pin image centered at the pin location
          // Pin anchor point is at the bottom center (tip of the pin)
          const pinImageX = pinX - (pinImage.width / 2);
          const pinImageY = pinY - pinImage.height; // Pin tip should be at pinY
          
          captureContext.drawImage(pinImage, pinImageX, pinImageY);
        } catch (error) {
          console.error('Error rendering pin icon, falling back to simple circle:', error);
          // Fallback to simple circle if SVG rendering fails
          const pinRadius = Math.max(12, 15 * (zoomLevel / 2));
          const borderWidth = Math.max(2, 3 * (zoomLevel / 2));
          
          captureContext.fillStyle = pinColor;
          captureContext.beginPath();
          captureContext.arc(pinX, pinY, pinRadius, 0, Math.PI * 2);
          captureContext.fill();
          
          captureContext.strokeStyle = '#FFFFFF';
          captureContext.lineWidth = borderWidth;
          captureContext.stroke();
        }
      }

      // Convert to base64 image data
      const outputFormat = outputOptions?.outputFormat ?? 'image/jpeg';
      const qualityInput = outputOptions?.imageQuality ?? this.DEFAULT_PREVIEW_JPEG_QUALITY;
      const quality = this.clampQuality(qualityInput);
      const imageData =
        outputFormat === 'image/png'
          ? captureCanvas.toDataURL(outputFormat)
          : captureCanvas.toDataURL(outputFormat, quality);

      return {
        imageData,
        pinPosition: pinPositionInImage
      };

    } catch (error) {
      console.error('Error capturing plan section:', error);
      throw error;
    }
  }

  /**
   * Captures the entire plan as an image
   * @param planFileUrl - The base64 data URL of the plan PDF
   * @param zoomLevel - The zoom level to capture at
   * @returns Promise<string> - Base64 image data
   */
  static async captureFullPlan(
    planFileUrl: string,
    zoomLevel: number = 1
  ): Promise<string> {
    try {
      const pdfjsLib = await getPdfJs();
      // Set up PDF.js worker
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

      // Load the PDF document
      const loadingTask = pdfjsLib.getDocument(planFileUrl);
      const pdf = await loadingTask.promise;

      // Get the first page
      const page = await pdf.getPage(1);
      
      // Create a canvas to render the PDF
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      if (!context) {
        throw new Error('Could not get canvas context');
      }

      // Calculate the viewport with the desired zoom level
      const viewport = page.getViewport({ scale: zoomLevel });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Render the PDF page to canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
        canvas,
      };

      await page.render(renderContext).promise;

      // Convert to base64 image data
      return canvas.toDataURL('image/jpeg', this.DEFAULT_PREVIEW_JPEG_QUALITY);

    } catch (error) {
      console.error('Error capturing full plan:', error);
      throw error;
    }
  }
}
